import { z } from 'zod';

/**
 * `pending-locator-reliability` v0.1 — wire shapes for the persistent
 * locator cache. Hashing stays in the runner + API (Node `crypto`) so
 * this module stays browser-safe for the contracts barrel.
 */

export const LocatorCacheKindSchema = z.enum(['promoted', 'recorded', 'healed', 'cache-hit']);
export type LocatorCacheKind = z.infer<typeof LocatorCacheKindSchema>;

export const LocatorCacheEntrySchema = z.object({
  urlPattern: z.string().min(1),
  intentHash: z.string().length(64),
  selector: z.string().min(1),
  kind: LocatorCacheKindSchema,
  confidence: z.number().min(0).max(1).optional(),
});
export type LocatorCacheEntry = z.infer<typeof LocatorCacheEntrySchema>;

export const LocatorCacheFlushSchema = z.object({
  urlPattern: z.string().min(1),
  intentHash: z.string().length(64),
  selector: z.string().min(1).optional(),
  kind: LocatorCacheKindSchema.optional(),
  confidence: z.number().min(0).max(1).optional(),
  ok: z.boolean(),
  flowId: z.string().optional(),
  stepId: z.string().optional(),
});
export type LocatorCacheFlush = z.infer<typeof LocatorCacheFlushSchema>;

export interface LocatorIntentParts {
  target?: string;
  label?: string;
  elementLabel?: string;
  role?: string;
}

/** Trim, collapse whitespace, lower-case. Locked concat for intent_hash. */
export function normalizeLocatorIntent(parts: LocatorIntentParts): string {
  const cell = (v: string | undefined) => (v || '').trim().replace(/\s+/g, ' ').toLowerCase();
  return `${cell(parts.target)}|${cell(parts.label)}|${cell(parts.elementLabel)}|${cell(parts.role)}`;
}

/**
 * `origin + pathname`, query/hash stripped, trailing slash normalised
 * so `/login` and `/login/` share a key. Invalid URLs return `''`.
 */
export function urlPatternFromPageUrl(raw: string): string {
  try {
    const u = new URL(raw);
    let path = u.pathname || '/';
    if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
    return `${u.origin}${path}`;
  } catch {
    return '';
  }
}

export function originFromBaseUrl(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return new URL(raw).origin;
  } catch {
    return '';
  }
}

/** Map a resolver strategy name to a persistable kind. `null` = do not write. */
export function kindFromStrategy(strategy: string, healed: boolean): LocatorCacheKind | null {
  const s = strategy.trim();
  if (s.startsWith('nl-reresolve')) return null;
  if (s.startsWith('cache:persistent')) return 'cache-hit';
  if (s.startsWith('promoted:')) return 'promoted';
  if (s.startsWith('recorded:')) return 'recorded';
  if (healed) return 'healed';
  return 'recorded';
}

// ---- v0.2 MCP resolve ----------------------------------------------------

export const LocatorResolveRequestSchema = z
  .object({
    intent: z.string().optional(),
    url: z.string().optional(),
    projectId: z.string().optional(),
    flowId: z.string().optional(),
    stepId: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    const hasStep = Boolean(val.flowId?.trim() && val.stepId?.trim());
    const hasIntent = Boolean(val.intent?.trim());
    if (!hasStep && !hasIntent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'intent is required unless flowId and stepId are both set',
        path: ['intent'],
      });
    }
  });
export type LocatorResolveRequest = z.infer<typeof LocatorResolveRequestSchema>;

export const LocatorResolveResponseSchema = z.object({
  selector: z.string().min(1),
  fallbacks: z.array(z.string()),
  strategy: z.string(),
  cached: z.boolean(),
  source: z.enum(['step', 'cache']),
});
export type LocatorResolveResponse = z.infer<typeof LocatorResolveResponseSchema>;

// ---- v0.3 editor chip ----------------------------------------------------

export const LocatorCacheChipSchema = z.object({
  id: z.string().uuid(),
  selector: z.string().min(1),
  kind: LocatorCacheKindSchema,
  urlPattern: z.string().min(1),
  confidence: z.number().min(0).max(1).optional(),
});
export type LocatorCacheChip = z.infer<typeof LocatorCacheChipSchema>;

export const LocatorCacheLookupQuerySchema = z.object({
  projectId: z.string().uuid(),
  flowId: z.string().uuid(),
  stepId: z.string().min(1).max(200),
});
export type LocatorCacheLookupQuery = z.infer<typeof LocatorCacheLookupQuerySchema>;

export const LocatorCacheLookupResponseSchema = z.object({
  entry: LocatorCacheChipSchema.nullable(),
});
export type LocatorCacheLookupResponse = z.infer<typeof LocatorCacheLookupResponseSchema>;

const MCP_INTENT_ROLES = [
  'button',
  'link',
  'textbox',
  'heading',
  'menuitem',
  'tab',
  'checkbox',
  'searchbox',
  'combobox',
] as const;

/**
 * Candidate intent tuples for a free-text MCP `intent`. Agents rarely
 * send the runner's four-cell concat, so we expand a few hashes
 * (target / label / elementLabel ± common roles) rather than miss.
 */
export function intentPartsFromFreeText(intent: string): LocatorIntentParts[] {
  const t = intent.trim();
  if (!t) return [];
  const out: LocatorIntentParts[] = [];
  const seen = new Set<string>();
  const push = (parts: LocatorIntentParts) => {
    const key = normalizeLocatorIntent(parts);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(parts);
  };

  if (t.startsWith('{')) {
    try {
      const obj = JSON.parse(t) as Record<string, unknown>;
      if (obj && typeof obj === 'object') {
        push({
          target: typeof obj.target === 'string' ? obj.target : undefined,
          label: typeof obj.label === 'string' ? obj.label : undefined,
          elementLabel: typeof obj.elementLabel === 'string' ? obj.elementLabel : undefined,
          role: typeof obj.role === 'string' ? obj.role : undefined,
        });
      }
    } catch {
      // fall through to free-text variants
    }
  }

  push({ target: t });
  push({ label: t });
  push({ elementLabel: t });
  push({ target: t, elementLabel: t });
  for (const role of MCP_INTENT_ROLES) {
    push({ target: t, role });
    push({ label: t, role });
    push({ elementLabel: t, role });
    push({ target: t, elementLabel: t, role });
  }
  return out;
}

export interface CanvasStepLike {
  id?: string;
  target?: string;
  role?: string;
  targetMeta?: {
    mode?: string;
    promotedSelector?: string;
    role?: string;
    label?: string;
    placeholder?: string;
  };
  recordedCandidates?: Array<{ kind?: string; selector?: string; confidence?: number }>;
}

const XPATH_RE = /^(?:\/\/|\.\/|\/html\b|xpath=)/i;
const MAX_RESOLVE_LOCATORS = 4;

function isIdentityCandidateKind(kind: string): boolean {
  const k = kind.trim().toLowerCase();
  if (!k) return false;
  if (k === 'id' || k === 'name' || k === 'label' || k === 'placeholder' || k === 'aria' || k === 'link text') {
    return true;
  }
  if (k.includes('test id') || k.includes('testid') || k.startsWith('data-')) return true;
  if (k.startsWith('role') || k.includes('aria')) return true;
  if (k === 'text' || k === 'link') return true;
  return false;
}

function isFragileSelector(sel: string): boolean {
  const t = sel.trim();
  if (!t) return true;
  const xpathBody = t.replace(/^xpath=/i, '');
  if (/^\/html\b/i.test(xpathBody) || /^\/\/html\b/i.test(xpathBody)) return true;
  if (/nth-(?:child|of-type)\s*\(/i.test(t)) return true;
  if ((t.match(/>/g) || []).length >= 3) return true;
  return false;
}

function looksLikeSelector(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  if (XPATH_RE.test(t)) return true;
  if (t.startsWith('#') || t.startsWith('.') || t.startsWith('[') || t.startsWith('text=')) return true;
  if (/^[a-z][\w-]*(\[|#|\.|:)/i.test(t)) return true;
  return false;
}

/**
 * Primary selector + identity fallbacks from a canvas step (MCP / API
 * resolve). Same identity-only rule as code-export: drop structural CSS
 * and absolute XPath. Cap = primary + 3.
 */
export function locatorsFromCanvasStep(step: CanvasStepLike): {
  selector: string;
  fallbacks: string[];
  strategy: string;
} | null {
  const chain: string[] = [];
  const seen = new Set<string>();
  const remember = (sel: string | undefined) => {
    const v = (sel || '').trim();
    if (!v || isFragileSelector(v) || seen.has(v)) return;
    seen.add(v);
    chain.push(v);
  };

  const meta = step.targetMeta;
  let strategy = 'recorded';
  if (meta?.mode === 'describe' && meta.promotedSelector) {
    remember(meta.promotedSelector);
    strategy = 'promoted';
  } else if (looksLikeSelector(step.target || '')) {
    remember(step.target);
  }

  const candidates = [...(step.recordedCandidates || [])].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
  for (const c of candidates) {
    if (!isIdentityCandidateKind(c.kind || '')) continue;
    remember(c.selector);
  }

  if (chain.length === 0) return null;
  const capped = chain.slice(0, MAX_RESOLVE_LOCATORS);
  return { selector: capped[0]!, fallbacks: capped.slice(1), strategy };
}
